import type { ScaleBand } from 'd3-scale';
import type { FairValueHistogramBucket } from '../fairValueHistogramFragment';
import type { CurrencyISO } from '@/constants/currencies';
import { formatterCurrencyTruncated } from '@/utilities/format';
import {
  MARKER_COLORS_CSS_VARS,
  measureTextWidth,
  TOOLTIP_FONT_SIZE,
  TOOLTIP_ITEM_SPACING,
  TOOLTIP_PADDING,
  TOOLTIP_RESERVED_HEIGHT,
} from '../utils';
import { Group } from '@visx/group';
import { Text } from '@visx/text';
import isNumber from 'lodash/isNumber';
import { useMemo } from 'react';

type Props = {
  selectedBucket: FairValueHistogramBucket | null;
  xBandScale: ScaleBand<number>;
  xMax: number;
  containerElement: HTMLElement | null;
  currencyIso: CurrencyISO;
  fairValue: number | null | undefined;
  sharePrice: number | null | undefined;
};

export const FairValueHistogramTooltip = ({
  selectedBucket,
  xBandScale,
  xMax,
  currencyIso,
  fairValue,
  sharePrice,
}: Props) => {
  const tooltipItems = useMemo(
    () =>
      [
        {
          label: 'Valuation Range',
          value: `${formatterCurrencyTruncated(
            selectedBucket?.min ?? 0,
            currencyIso,
            2
          )} - ${formatterCurrencyTruncated(
            selectedBucket?.max ?? 0,
            currencyIso,
            2
          )}`,
          labelColor: 'var(--s-color-text-soft)',
        },
        {
          label: 'Followers',
          value: `${selectedBucket?.primaryCount ?? 0}`,
          labelColor: 'var(--s-color-text-soft)',
        },
        isNumber(fairValue) && {
          label: 'My Fair Value',
          value: formatterCurrencyTruncated(fairValue, currencyIso, 2),
          labelColor: MARKER_COLORS_CSS_VARS.MY_FAIR_VALUE,
        },
        {
          label: 'Last Price',
          value: isNumber(sharePrice)
            ? formatterCurrencyTruncated(sharePrice, currencyIso, 2)
            : '-',
          labelColor: MARKER_COLORS_CSS_VARS.SHARE_PRICE,
        },
      ].filter(Boolean) as {
        label: string;
        value: string;
        labelColor: string;
      }[],
    [selectedBucket, fairValue, sharePrice, currencyIso]
  );

  const longestLabel = useMemo(() => {
    return tooltipItems.reduce(
      (acc, item) =>
        Math.max(acc, measureTextWidth(item.label, TOOLTIP_FONT_SIZE)),
      0
    );
  }, [tooltipItems]);

  const longestValue = useMemo(() => {
    return tooltipItems.reduce(
      (acc, item) =>
        Math.max(acc, measureTextWidth(item.value, TOOLTIP_FONT_SIZE)),
      0
    );
  }, [tooltipItems]);

  const tooltipHeight =
    tooltipItems.length * TOOLTIP_FONT_SIZE +
    TOOLTIP_ITEM_SPACING * (tooltipItems.length - 1) +
    TOOLTIP_PADDING * 2;
  const tooltipWidth =
    longestLabel + TOOLTIP_ITEM_SPACING + longestValue + TOOLTIP_PADDING * 2;
  const bucketX = xBandScale(selectedBucket?.min ?? 0) ?? 0;
  const bucketWidth = xBandScale.bandwidth();
  const bucketXCenter = bucketX + bucketWidth / 2;
  const getTooltipX = () => {
    const tooltipXMax = xMax - tooltipWidth;
    const tooltipX = bucketXCenter - tooltipWidth / 2;
    return Math.max(0, Math.min(tooltipX, tooltipXMax));
  };

  return (
    <Group
      left={getTooltipX()}
      top={TOOLTIP_RESERVED_HEIGHT - tooltipHeight}
      role="tooltip"
    >
      <rect
        x={0}
        y={0}
        width={tooltipWidth}
        height={tooltipHeight}
        className="fill-p-black light:fill-transparentSubtle"
      />
      <Group
        top={TOOLTIP_PADDING}
        left={TOOLTIP_PADDING}
        height={tooltipHeight - TOOLTIP_PADDING * 2}
        width={tooltipWidth - TOOLTIP_PADDING * 2}
      >
        {tooltipItems.map((item, index) => (
          <Group
            key={index}
            left={0}
            top={
              TOOLTIP_ITEM_SPACING +
              (index === 0
                ? 0
                : index * (TOOLTIP_FONT_SIZE + TOOLTIP_ITEM_SPACING))
            }
          >
            <Text
              fontSize={TOOLTIP_FONT_SIZE}
              x={0}
              fill={item.labelColor}
              textAnchor="start"
            >
              {item.label}
            </Text>
            <Text
              fontSize={TOOLTIP_FONT_SIZE}
              x={longestLabel + TOOLTIP_ITEM_SPACING}
              className="fill-solid"
              textAnchor="start"
            >
              {item.value}
            </Text>
          </Group>
        ))}
      </Group>
    </Group>
  );
};
